import { DECISIONS_COPY } from '../copy';
import type { OrgUnitDto, TemplateDecisionContextDto } from '@pams/shared';

/**
 * [P7B §3b] The decisions page's pure rules — kept out of the component file, both because a test
 * reads them without a render and because `react-refresh/only-export-components` refuses a helper
 * exported beside a component.
 */

/**
 * ⚠️ [P7B §3c] "CREATE A POLICY COVERING THESE" IS A LINK TO THE EXISTING BUILDER, PREFILLED BY URL —
 * never a policy created by this page. The policy author reviews, saves and publishes it through the
 * ordinary flow, then comes back and chooses it here.
 */
export const createPolicyHref = (
  setKey: string,
  label: string,
  columns: readonly { kind: 'POST' | 'VEHICLE'; id: string }[],
): string => {
  const params = new URLSearchParams();
  const posts = columns.filter((column) => column.kind === 'POST').map((column) => column.id);
  const vehicles = columns.filter((column) => column.kind === 'VEHICLE').map((column) => column.id);
  if (posts.length > 0) params.set('postTypeIds', posts.join(','));
  if (vehicles.length > 0) params.set('vehicleTypeIds', vehicles.join(','));
  params.set('name', `Durga Puja — ${label}`.slice(0, 160));
  params.set('from', 'templates');
  params.set('setKey', setKey);
  return `/policies/new?${params.toString()}`;
};

/**
 * ⚠️ [P7B.1 §3b] "CREATE A POST TYPE FOR THIS COLUMN" IS A LINK TO THE POST TYPE MASTER'S OWN FORM,
 * PREFILLED BY URL — never a post type created by this page, the policy link's precedent above. A post
 * type is a column every future policy can carry, so creating one is a super admin's own Save in that
 * drawer, with its own audit row. Named as the booklet names the column, it then resolves the column BY
 * RULE for every template asking for it — the decision is made by making the master, not recorded here.
 *
 * Name and short name start as the booklet's own words, cut to the master's limits (120 and 40);
 * `column` carries the words whole, for the way back to this decision.
 */
export const createPostTypeHref = (setKey: string, column: string): string => {
  const params = new URLSearchParams();
  params.set('new', '1');
  params.set('name', column.slice(0, 120));
  params.set('shortName', column.slice(0, 40));
  params.set('column', column);
  params.set('from', 'templates');
  params.set('setKey', setKey);
  return `/post-types?${params.toString()}`;
};

export type TotalsContext = Extract<TemplateDecisionContextDto, { class: 'TOTALS' }>;

/** One cell of the corrections — a duty point's key and a column, joined by a separator neither holds. */
export const cellKey = (dutyPointKey: string, column: string): string => `${dutyPointKey}::${column}`;

type TotalsPoint = TotalsContext['dutyPoints'][number];

/** The rows a duty point holds in a column — one per source, so there may be more than one. */
export const cellsIn = (point: TotalsPoint, column: string): TotalsPoint['rows'] =>
  point.rows.filter((row) => row.column === column);

/** The duty points that hold at least one row in a column — the only ones a correction may name. */
export const pointsWithColumn = (context: TotalsContext, column: string): TotalsPoint[] =>
  context.dutyPoints.filter((point) => cellsIn(point, column).length > 0);

/**
 * The figure a column comes to, with `values` standing in for the rows they name — THE SERVER'S
 * ARITHMETIC exactly (`assertDecisionHolds`' `PRINTED` arm): the column's in-charge count, plus every
 * row of every covered duty point, where a corrected (duty point, column) replaces EACH of that duty
 * point's rows in the column. A client sum that differs from the server's would offer a Save the PUT
 * refuses, or refuse one it would accept.
 */
export const columnSum = (
  context: TotalsContext,
  column: string,
  values: Readonly<Record<string, number>> = {},
): number => {
  const inCharge = context.columns.find((entry) => entry.column === column)?.inCharge ?? 0;
  return context.dutyPoints.reduce(
    (total, point) =>
      cellsIn(point, column).reduce((sum, cell) => sum + (values[cellKey(point.key, column)] ?? cell.n), total),
    inCharge,
  );
};

/**
 * ⚠️ THE COLUMNS A PERSON CORRECTS: those the server marks `correctable` (the table's duty points are
 * known and the column is a force column of theirs) whose printed figure the rows as read do not
 * reach. A differing column that is NOT correctable can only be taken as read or held — offering
 * inputs for it would send rows the server refuses.
 */
export const correctableColumns = (context: TotalsContext): { column: string; printed: number }[] =>
  context.columns.flatMap((column) =>
    column.correctable && column.printed !== null && column.printed !== columnSum(context, column.column)
      ? [{ column: column.column, printed: column.printed }]
      : [],
  );

/**
 * ⚠️ EACH CORRECTED COLUMN MUST COME TO ITS PRINTED FIGURE — the PRINTED choice is "the printed total,
 * reached by correcting the rows", and a table total cannot be apportioned to duty points by a program.
 * `undefined` when every column does; otherwise the first column that does not, in a sentence.
 */
export const printedTotalsProblem = (
  context: TotalsContext,
  values: Readonly<Record<string, number>>,
): string | undefined => {
  for (const { column, printed } of correctableColumns(context)) {
    const sum = columnSum(context, column, values);
    if (sum !== printed) return `${column}: ${DECISIONS_COPY.totalsSum(sum, printed)}. ${DECISIONS_COPY.totalsMustSum}`;
  }
  return undefined;
};

/**
 * The rows a PRINTED decision sends: only the (duty point, correctable column) cells a person typed —
 * the server leaves every row nobody named at its figure as read, so sending an untouched cell would
 * be a correction nobody made (and, on a duty point holding two rows in one column, a wrong one). A
 * cell is only ever sent for a duty point that holds a row in that column, which the server requires.
 */
export const printedRows = (
  context: TotalsContext,
  values: Readonly<Record<string, number>>,
): { dutyPointKey: string; column: string; n: number }[] =>
  correctableColumns(context).flatMap(({ column }) =>
    pointsWithColumn(context, column).flatMap((point) => {
      const n = values[cellKey(point.key, column)];
      return n === undefined ? [] : [{ dutyPointKey: point.key, column, n }];
    }),
  );

/**
 * ⚠️ [P7B §3b] THE UNITS A `SOURCE` MAY NOT NAME — the server's own rule (`templateMasters.service.ts`'s
 * `divisionOf`): an allocation names a DIVISION, or a UNIT beneath one, and nothing else. The
 * commissionerate itself, and any unit with no division above it, is refused with a 400; offering it
 * in the picker would be a choice the PUT refuses. Read from the same tree the picker renders.
 */
export const unallocatableUnitIds = (units: readonly Pick<OrgUnitDto, 'id' | 'type' | 'ancestors'>[]): string[] => {
  const typeOf = new Map(units.map((unit) => [unit.id, unit.type] as const));
  return units
    .filter(
      (unit) =>
        unit.type !== 'DIVISION' &&
        !(unit.type === 'UNIT' && unit.ancestors.some((ancestor) => typeOf.get(ancestor) === 'DIVISION')),
    )
    .map((unit) => unit.id);
};
